import React, { useState, useEffect, useMemo } from 'react';
import * as AppPics from 'assets/images';
import { Link, useHistory, useLocation } from 'react-router-dom';
import useCategory from 'hooks/API/GET/useCategory';
import TablePaginate from 'components/Paginate';
import usePaginateDefaults from 'hooks/usePaginateDefaults';
import useDeleteCategory from 'hooks/API/DELETE/useDeleteCategory';
import { notifier } from 'components/Toaster';
import BasicDelete from 'components/molecules/Modal/Delete/BasicDelete';
import { useQueryClient } from 'react-query';
import useDebounce from 'hooks/useDebounce';
import { AppRadioBox } from 'components';
import AppBreadcrumbs from 'components/molecules/AppBreadcrumbs';
import { ArrowsPng } from 'assets/images';

const actionsConfig = {
  open: false,
  data: {},
  Header: null,
  Body: null,
  isDeleteOperation: false,
};

function CategoryList() {
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const queryParams = new URLSearchParams(location.search);
  const redirectBack = queryParams.get('redirect_back');

  const urlParams = new URLSearchParams(location.search);
  const initialKeyword = urlParams.get('keyword') || '';
  const initialSection = urlParams.get('section') || '';
  const initialPage = parseInt(urlParams.get('page')) || 0;

  const [params, setParams] = usePaginateDefaults({ limit: 5, page: initialPage });
  const [search, setSearch] = useState(initialKeyword);
  const [searchTerm, setSearchTerm] = useState(initialKeyword);
  const [selectedType, setSelectedType] = useState(initialSection);
  const [actions, setActions] = useState({ ...actionsConfig });
  const [enableFilter, setEnableFilter] = useState(!!initialSection);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Handle URL parameter changes (similar to EpisodeList)
  useEffect(() => {
    const updatedParams = new URLSearchParams(location.search);
    const newKeyword = updatedParams.get('keyword') || '';
    const newSection = updatedParams.get('section') || '';
    const newPage = parseInt(updatedParams.get('page')) || 0;

    setSearchTerm(newKeyword);
    setSearch(newKeyword);
    setSelectedType(newSection);
    setParams((prev) => ({ ...prev, page: newPage }));
    setEnableFilter(!!newSection);
  }, [location.key]);

  const debouncedSearch = useDebounce(search, 600);

  const updateURL = (newParams) => {
    const currentParams = new URLSearchParams(location.search);

    Object.entries(newParams).forEach(([key, value]) => {
      if (value && typeof value === 'string' && value.trim() !== '') {
        currentParams.set(key, value);
      } else {
        currentParams.delete(key);
      }
    });

    const newSearch = currentParams.toString();
    const newURL = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;

    if (newURL !== location.pathname + location.search) {
      history.replace(newURL);
    }
  };

  useEffect(() => {
    updateURL({
      keyword: debouncedSearch,
      section: selectedType,
      page: params.page,
    });
  }, [debouncedSearch, selectedType, params.page]);

  const categoryParams = useMemo(
    () => ({
      ...params,
      keyword: debouncedSearch,
      section: selectedType || undefined,
    }),
    [params, debouncedSearch, selectedType]
  );

  const { data, isLoading, refetch } = useCategory({
    enabled: true,
    config: { params: categoryParams },
    queryKey: ['podcast-category', categoryParams],
  });

  // const categories = data?.data?.data?.result || [];
  const categories = [...(data?.data?.data?.result || [])].sort(
    (a, b) => (a.sortPosition || 0) - (b.sortPosition || 0)
  );
  const { meta: { page = 1, total = 0 } = {} } = data?.data?.data || {};

  const { mutate: deleteCategory } = useDeleteCategory();

  const handleDelete = (category) => {
    setSelectedCategory(category);
    setActions({
      open: true,
      data: { id: category._id },
      Header: 'Delete Genre',
      Body: `Are you sure you want to delete "${category?.categoryName}"?`,
      isDeleteOperation: true,
    });
  };

  const handleDeleteConfirm = () => {
    deleteCategory(actions.data.id, {
      onSuccess: (res) => {
        notifier.success(res?.data?.data?.message);
        queryClient.invalidateQueries('podcast-category');
      },
      onError: (err) => {
        notifier.error(err?.response?.data?.data?.message);
      },
    });
    setActions({ ...actionsConfig });
  };

  const performSearch = (value) => {
    setSearch(value);
    setSearchTerm(value);
    setParams((prev) => ({ ...prev, page: 0 }));
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value === '') {
      performSearch('');
    }
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
    setParams((prev) => ({ ...prev, page: 0 }));
  };

  const clearAllFilters = () => {
    setSelectedType('');
    performSearch('');
    setParams((prev) => ({ ...prev, page: 0 }));
    setEnableFilter(false);
    history.replace(location.pathname);
  };

  // Handle back button navigation (similar to EpisodeList)
  const handleBackNavigation = () => {
    if (redirectBack) {
      history.push(decodeURIComponent(redirectBack));
    } else {
      history.push('/podcast');
    }
  };

  return (
    <>
      <BasicDelete
        open={actions.open}
        closeModal={() => setActions({ ...actionsConfig })}
        Header={actions.Header}
        Body={actions.Body}
        handleConfirmation={handleDeleteConfirm}
      />

      <div className="mainWraper coursetabsection">
        <div className="tab">
          <ul className="tab__menu">
            <li className="back">
              <button className="btn-back" onClick={handleBackNavigation}>
                <img src={AppPics.BackSvg} alt="Back" />
              </button>
            </li>
          </ul>

          <div
            className="tab__content
          ___adjust___tab___padding"
          >
            <div className="tab__content-actHead">
              <AppBreadcrumbs
                links={[
                  { label: 'Dashboard', to: '/dashboard' },
                  { label: 'Podcast Home', to: '/podcast' },
                ]}
                current="Genre"
              />
            </div>
            <div className="tab__content-actBody">
              <div className="px-5 py-5">
                <div className="tab__contentMenu">
                  <ul className="tab__contentMenu-list">
                    <li>
                      <h2 className="prime-head p-0 boldtitle text-nowrap">Manage Podcast Genre</h2>
                    </li>
                  </ul>

                  <div className="mainHeader__search">
                    <ul className="tab__contentMenu-list">
                      <li className="relative">
                        <input
                          className="mainHeader__searchInput"
                          placeholder="Search Genre..."
                          type="text"
                          value={searchTerm}
                          onChange={handleSearchChange}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              performSearch(searchTerm);
                            }
                          }}
                        />
                        <span
                          className="mainHeader__searchBtn"
                          onClick={() => performSearch(searchTerm)}
                        >
                          <img src={AppPics.TopMenuSearchSvg} alt="Search" />
                        </span>
                      </li>
                      <li className="px-4 flex items-center">
                        <button
                          type="button"
                          onClick={() => setEnableFilter(!enableFilter)}
                          className="flex items-center gap-1"
                        >
                          <img src={AppPics.TabFilterSvg} alt="Filter" />
                          <span className="viewsettingtitle">Filter</span>
                        </button>
                      </li>
                    </ul>
                  </div>

                  <div className="flex items-center gap-10">
                    {!enableFilter && !selectedType && !search && (
                      <Link to="/podcast/category/reorder" className="mr-4">
                        <img
                          src={ArrowsPng}
                          alt="Reorder Genre"
                          title="Reorder Genre"
                          className="cursor-pointer"
                        />
                      </Link>
                    )}

                    <Link to="/podcast/category/add">
                      <button className="btn-left-content add">New Genre</button>
                    </Link>
                  </div>
                </div>
                <div className="tab__content-actBody">
                  {enableFilter && (
                    <div className="row mt-3">
                      <div className="col-lg-3">
                        <label className="label d-block">Genre section</label>
                        <div className="radio-group">
                          <AppRadioBox
                            inputProps={{
                              name: 'section',
                              value: 'trending',
                              checked: selectedType === 'trending',
                              onChange: () => handleTypeChange('trending'),
                            }}
                            labelProps={{ label: 'Trending' }}
                          />
                          <AppRadioBox
                            inputProps={{
                              name: 'section',
                              value: 'popular',
                              checked: selectedType === 'popular',
                              onChange: () => handleTypeChange('popular'),
                            }}
                            labelProps={{ label: 'Popular' }}
                          />
                        </div>
                      </div>

                      <div className="col-lg-3 d-flex">
                        <button
                          type="button"
                          className="btn btn-plane p--tint"
                          style={{ alignSelf: 'end' }}
                          onClick={clearAllFilters}
                        >
                          Clear filter
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="tab__contentBody">
                    <div className="tableWrap">
                      <div className="table-responsive">
                        <table className="table tableWrap__table">
                          <thead className="thead-light __appTheadColor">
                            <tr>
                              <th className="text-center">#</th>
                              <th className="text-center">Genre Name</th>
                              <th className="text-center">Key</th>
                              <th className="text-center">Status</th>
                              <th className="text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {isLoading ? (
                              <tr>
                                <td colSpan={4} className="text-center py-4">
                                  Loading Genre...
                                </td>
                              </tr>
                            ) : categories.length > 0 ? (
                              categories.map((category, idx) => (
                                <tr key={category._id}>
                                  <td className="text-center">
                                    {(page - 1) * params.limit + idx + 1}
                                  </td>
                                  <td className="text-center">{category.categoryName}</td>
                                  <td className="text-center">{category.key}</td>
                                  <td className="text-center">
                                    {category?.isPublished ? 'Published' : 'Not Published'}
                                  </td>
                                  <td className="text-center">
                                    <Link
                                      className="actions-icons mx-2 text-primary"
                                      title="Edit"
                                      to={{
                                        pathname: `/podcast/category/${category._id}`,
                                        search: `?redirect_back=${encodeURIComponent(
                                          location.pathname + location.search
                                        )}`,
                                      }}
                                    >
                                      <img src={AppPics.EditSvg} alt="Edit" />
                                    </Link>

                                    <button
                                      className="actions-icons mx-2 text-danger"
                                      title="Delete"
                                      onClick={() => handleDelete(category)}
                                    >
                                      <img src={AppPics.DeleteSvg} alt="Delete" />
                                    </button>
                                  </td>
                                </tr>
                              ))
                            ) : debouncedSearch.trim() !== '' ? (
                              <tr>
                                <td colSpan={4} className="text-center py-4">
                                  No Genre found for "{debouncedSearch}"
                                </td>
                              </tr>
                            ) : (
                              <tr>
                                <td colSpan={4} className="text-center py-4">
                                  No Genre available.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <TablePaginate
                      currentPage={page}
                      maxCount={total}
                      defaultLimit={params.limit}
                      setPage={(page) => setParams((prev) => ({ ...prev, page }))}
                      handleChange={(limit) => setParams((prev) => ({ ...prev, limit, page: 0 }))}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default CategoryList;
